import { User, RewardActivity } from '../types';
import { addDays, subDays, format, differenceInDays, isSameDay, parseISO } from 'date-fns';

// Mock current user (would come from authentication in a real app)
export const currentUser: User = {
  id: "u1",
  name: "Alex Johnson",
  email: "alex@example.com", 
  rewardBalance: 50,
  lastRewardClaim: format(subDays(new Date(), 1), "yyyy-MM-dd'T'HH:mm:ss"),
  lastClaimDate: format(subDays(new Date(), 1), "yyyy-MM-dd"),
  isAdmin: true,
  usedTools: ["t1", "t3"],
  inviteLink: "https://hitry.ai/invite/alexj", 
  notificationSettings: {
    dailyReminder: true
  },
  claimStreak: 0
};

// Mock reward activities
export const rewardActivities: RewardActivity[] = [
  {
    id: "ra1",
    userId: "u1",
    description: "Initial balance",
    amount: 50,
    timestamp: format(subDays(new Date(), 1), "yyyy-MM-dd'T'HH:mm:ss")
  }
];

// Calculate reward totals
export const getRewardTotals = () => {
  const today = format(new Date(), 'yyyy-MM-dd');
  const weekStart = format(subDays(new Date(), 7), 'yyyy-MM-dd');
  
  const todayTotal = rewardActivities
    .filter(activity => activity.timestamp.startsWith(today))
    .reduce((sum, activity) => sum + activity.amount, 0);
    
  const weekTotal = rewardActivities
    .filter(activity => {
      const activityDate = activity.timestamp.split('T')[0];
      return activityDate >= weekStart && activityDate <= today;
    })
    .reduce((sum, activity) => sum + activity.amount, 0);
    
  const allTimeTotal = rewardActivities
    .reduce((sum, activity) => sum + activity.amount, 0);
    
  return { today: todayTotal, week: weekTotal, allTime: allTimeTotal };
};

// Check if user can claim daily reward
export const canClaimDailyReward = (user: User): boolean => {
  if (!user.lastRewardClaim) return true;
  
  const lastClaim = new Date(user.lastRewardClaim);
  const nextClaimDate = addDays(lastClaim, 1);
  return new Date() >= nextClaimDate;
};

// Get next claim time
export const getNextClaimTime = (user: User): Date | null => {
  if (!user.lastRewardClaim) return null;
  
  const lastClaim = new Date(user.lastRewardClaim);
  return addDays(lastClaim, 1);
};

// Mock function to claim daily reward
export const claimDailyReward = (user: User): { user: User, activity: RewardActivity } => {
  const now = new Date();
  const today = format(now, 'yyyy-MM-dd');
  const lastClaimDate = user.lastClaimDate ? parseISO(user.lastClaimDate) : null;
  
  // Calculate new streak
  let newStreak = 1; // Start with 1 for the first claim
  if (lastClaimDate) {
    const daysSinceLastClaim = differenceInDays(now, lastClaimDate);
    if (daysSinceLastClaim === 1) {
      // If claimed exactly one day after the last claim, increment streak
      newStreak = user.claimStreak + 1;
    }
  }
  
  // Calculate reward amount
  let totalReward = 50;
  let streakBonus = 0;
  
  // Special bonus for 7-day streak
  if (newStreak === 7) {
    streakBonus = 500;
    totalReward += streakBonus;
  }
  
  const updatedUser = { 
    ...user, 
    rewardBalance: user.rewardBalance + totalReward,
    lastRewardClaim: format(now, "yyyy-MM-dd'T'HH:mm:ss"),
    lastClaimDate: today,
    claimStreak: newStreak
  };
  
  const activity: RewardActivity = {
    id: `ra${rewardActivities.length + 1}`,
    userId: user.id,
    description: streakBonus > 0 
      ? `Daily login reward (7 day streak achieved! +${streakBonus} bonus)`
      : `Daily login reward (${newStreak} day streak)`,
    amount: totalReward,
    timestamp: format(now, "yyyy-MM-dd'T'HH:mm:ss")
  };
  
  rewardActivities.push(activity);
  
  return { user: updatedUser, activity };
};

// Mock function to record tool usage
export const recordToolUse = (user: User, toolId: string, amount: number): { user: User, activity: RewardActivity } => {
  const now = new Date();
  const toolName = toolId; // In a real app, get actual name from tool object
  
  // Only add to usedTools if not already there
  const usedTools = user.usedTools.includes(toolId) 
    ? user.usedTools 
    : [...user.usedTools, toolId];
    
  const updatedUser = { 
    ...user, 
    rewardBalance: user.rewardBalance + amount,
    usedTools
  };
  
  const activity: RewardActivity = {
    id: `ra${rewardActivities.length + 1}`,
    userId: user.id,
    description: `Used ${toolName} tool`,
    amount,
    timestamp: format(now, "yyyy-MM-dd'T'HH:mm:ss")
  };
  
  rewardActivities.push(activity);
  
  return { user: updatedUser, activity };
};

// Get admin statistics (visible to admin only)
export const getAdminStats = () => {
  return {
    totalUsers: 127,
    dailyActiveUsers: 43,
    totalToolClicks: 954,
    totalSignups: 286,
    totalTokensIssued: 21000000,
    remainingTokens: 20950000
  };
};